import { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import {
  Home,
  Menu as MenuIcon,
  UserCircle,
  ChevronLeft,
  ChevronRight,
  Search,
  Cloud,
  BarChart3,
  Network,
  ShieldEllipsis,
  Settings,
  Users,
  LogOut,
  Calendar,
  DollarSign,
} from "lucide-react";
import Searchbar from "./Searchbar";
import { useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
export default function Navbar() {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [search, setSearch] = useState(false);
  const [selectedItem, setSelectedItem] = useState("/");
  const navigate = useNavigate();
  const profileMenuRef = useRef(null);

  let idToken = useSelector((state) => state.idToken.value);
  let userName = "User";
  try {
    const decoded = jwtDecode(idToken);
    userName = `${decoded.given_name} ${decoded.family_name}`;
  } catch (err) {
    console.log("Error decoding token: ", err);
  }

  const location = useLocation();

  useEffect(() => {
    if (location.pathnam) setSelectedItem(location.pathname);
  }, [location]);

  const navigation = [
    { name: "Home", icon: Home, href: "/" },
    {
      name: "Search",
      icon: Search,
      onClick: () => {
        setSearch(true);
      },
    },
    { name: "Endpoint", icon: Network, href: "/endpoint" },
    { name: "Performance", icon: BarChart3, href: "/performance" },
    { name: "Log Monitor", icon: Cloud, href: "/log-monitor-in-progress" },
    { name: "Security", icon: ShieldEllipsis, href: "/security" },
    { name: "Resource Scheduler", icon: Calendar, href: "/resourceScheduler" },
    { name: "Cost Management", icon: DollarSign, href: "/cost" },
  ];

  const settings = [
    { name: "Platform Configuration", icon: Settings, href: "/config" },
    { name: "User Management", icon: Users, href: "/usermanagement" },
  ];

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login", { replace: true });
  };

  const isPathActive = (path) => {
    if (path === "/" && location.pathname === "/") {
      return true;
    }
    return path !== "/" && location.pathname.startsWith(path);
  };
  const NavItem = ({ item }) => {
    const isSelected = item.href && isPathActive(item.href);
    // If item has href, use <Link>. If only onClick, use <button>.
    if (item.href) {
      return (
        <div className="relative">
          <Link
            to={item.href}
            className={`group flex items-center w-full px-4 py-2.5 text-sm text-[#003366] rounded-md transition-all duration-200 ${
              isSelected ? "bg-gray-100" : "hover:bg-[#003366]/5"
            }`}
          >
            <div className="relative">
              <item.icon className="h-5 w-5 flex-shrink-0 text-[#003366] group-hover:text-[#003366]" />
              {!isExpanded && (
                <div className="fixed ml-2 left-20 bg-gray-800 text-white px-2 py-1 rounded text-sm whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  {item.name}
                </div>
              )}
            </div>
            {isExpanded && (
              <span className="ml-3 font-medium opacity-90 group-hover:opacity-100">
                {item.name}
              </span>
            )}
          </Link>
        </div>
      );
    }
    // Fallback to button for items like Search
    return (
      <div className="relative">
        <button
          onClick={item.onClick}
          className={`group flex items-center w-full px-4 py-2.5 text-sm text-[#003366] rounded-md transition-all duration-200 hover:bg-[#003366]/5`}
        >
          <div className="relative">
            <item.icon className="h-5 w-5 flex-shrink-0 text-[#003366] group-hover:text-[#003366]" />
            {!isExpanded && (
              <div className="fixed ml-2 left-20 bg-gray-800 text-white px-2 py-1 rounded text-sm whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                {item.name}
              </div>
            )}
          </div>
          {isExpanded && (
            <span className="ml-3 font-medium opacity-90 group-hover:opacity-100">
              {item.name}
            </span>
          )}
        </button>
      </div>
    );
  };

  const toggleSidebar = () => {
    setIsExpanded(!isExpanded);
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target)
      ) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileMenuRef]);
  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Mobile menu button */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-md bg-white shadow-md"
      >
        <MenuIcon className="h-6 w-6 text-[#003366]" />
      </button>

      {/* Sidebar */}
      <div
        className={`
          fixed inset-y-0 left-0 transform 
          ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
          ${isExpanded ? "w-64" : "w-20"}
          lg:relative lg:translate-x-0
          bg-white border-r border-gray-200 transition-all duration-200 ease-in-out
          flex flex-col shadow-sm z-40 h-screen
        `}
      >
        {/* Toggle Button */}
        <button
          onClick={toggleSidebar}
          className="absolute -right-3 top-10 bg-white border border-gray-200 rounded-full p-1.5 cursor-pointer hidden lg:block hover:bg-gray-50 transition-colors duration-200 shadow-sm"
        >
          {isExpanded ? (
            <ChevronLeft className="h-5 w-5 text-[#003366]" />
          ) : (
            <ChevronRight className="h-5 w-5 text-[#003366]" />
          )}
        </button>

        {/* Logo Section */}
        <div className="p-4 flex items-center justify-center border-b border-gray-200">
          {!isExpanded ? (
            <img
              src="/logo-cloud.png"
              alt="CloudLens"
              className="h-9 w-9 object-contain"
              onClick={() => navigate("/")}
            />
          ) : (
            <div className="flex items-center gap-2">
              <img
                src="/logo-cloud.png"
                alt="CloudLens"
                className="h-9 w-9 object-contain"
              />
              <span className="text-[#003366] font-semibold text-lg">
                CloudLens
              </span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex-1 flex flex-col h-full">
          <div className="flex-1 overflow-y-auto py-4">
            <div className="px-3 space-y-1">
              {navigation.map((item) => (
                <NavItem key={item.name} item={item} />
              ))}
            </div>

            <div className="my-4 mx-3 border-t border-gray-200" />

            <div className="px-3 space-y-1">
              {settings.map((item) => (
                <NavItem key={item.name} item={item} />
              ))}
            </div>
          </div>

          {/* Profile & Logout */}
          <div className="border-t border-gray-200 p-4 mt-auto">
            <div className="relative">
              <button
                onClick={() => {
                  setIsExpanded(true);
                  setIsProfileOpen(!isProfileOpen);
                }}
                className="group flex items-center w-full px-4 py-2.5 text-sm text-[#003366] hover:bg-[#003366]/5 rounded-md transition-all duration-200"
              >
                <div className="relative">
                  <UserCircle className="h-5 w-5 flex-shrink-0 text-[#003366]" />
                  {!isExpanded && (
                    <div className="fixed ml-2 left-20 bg-gray-800 text-white px-2 py-1 rounded text-sm whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      {userName}
                    </div>
                  )}
                </div>
                {isExpanded && (
                  <span className="ml-3 font-medium">{userName}</span>
                )}
                {isProfileOpen && (
                  <div
                    ref={profileMenuRef}
                    className="absolute right-0 bottom-full mb-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50"
                  >
                    <>
                      <button
                        onClick={() => navigate("/usersettings")}
                        className="block w-full text-left px-4 py-2 text-sm text-[#003366] hover:bg-[#003366]/5 flex items-center"
                      >
                        <Settings className="h-5 w-5 mr-2" />
                        Profile Settings
                      </button>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-[#003366] hover:bg-[#003366]/5 flex items-center"
                      >
                        <LogOut className="h-5 w-5 mr-2" />
                        Logout
                      </button>
                    </>
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6">
          <div className="outlet-container bg-white rounded-lg shadow-sm p-6">
            <Outlet />
          </div>
        </div>
      </main>

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 lg:hidden z-30"
          onClick={() => setIsMobileOpen(false)}
        />
      )}
      {search ? <Searchbar search={search} onClose={setSearch} /> : <></>}
    </div>
  );
}
